/**
 * 서명 패드.
 *
 * 레거시는 OZ eForm 의 SignPad 컴포넌트(ActiveX 계열 뷰어 의존)를 썼고,
 * 결과는 이미지 하나뿐이라 사후 검증이 불가능했다.
 *
 * 여기서는 PNG 와 함께 **정규화(0~1) 획순 데이터**를 남긴다. 좌표를 정규화하는
 * 이유는 기기 해상도가 달라도 같은 서명으로 비교할 수 있어야 하기 때문이다.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface SignaturePoint {
  x: number;
  y: number;
  t: number;
}

export interface SignatureResult {
  dataUrl: string;
  strokes: SignaturePoint[][];
}

interface Props {
  label: string;
  value?: SignatureResult | null;
  onChange: (result: SignatureResult | null) => void;
  height?: number;
  disabled?: boolean;
}

const LINE_WIDTH = 2.2;
/** 이 획수/점수 미만이면 실수로 찍은 점으로 본다. */
const MIN_POINTS = 8;

export function SignaturePad({ label, value, onChange, height = 180, disabled }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<SignaturePoint[][]>([]);
  const drawingRef = useRef(false);
  const startedAtRef = useRef(0);
  const [empty, setEmpty] = useState(true);

  /** CSS 픽셀과 실제 픽셀을 맞춘다. 안 맞추면 고해상도 기기에서 뭉개진다. */
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111';

    // canvas.width 대입은 비트맵을 비운다. 회전·키보드 등으로 리사이즈되면 상태(strokes)는
    // '서명됨'인데 화면만 빈 칸이 되므로, 정규화 좌표(0~1)로 저장한 획을 다시 그린다.
    for (const stroke of strokesRef.current) {
      for (let i = 1; i < stroke.length; i += 1) {
        const from = toCanvas(stroke[i - 1]!, rect);
        const to = toCanvas(stroke[i]!, rect);
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
      }
    }
  }, []);

  useEffect(() => {
    setupCanvas();
    window.addEventListener('resize', setupCanvas);
    return () => window.removeEventListener('resize', setupCanvas);
  }, [setupCanvas]);

  // 외부에서 값이 주입되면(= propagate 로 복사됨) 캔버스에 그려준다.
  useEffect(() => {
    if (!value) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const img = new Image();
    img.onload = () => {
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
      ctx.drawImage(img, 0, 0, rect.width, rect.height);
      setEmpty(false);
    };
    img.src = value.dataUrl;
    strokesRef.current = value.strokes;
  }, [value]);

  function pointFrom(event: React.PointerEvent<HTMLCanvasElement>): SignaturePoint {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: Number(((event.clientX - rect.left) / rect.width).toFixed(4)),
      y: Number(((event.clientY - rect.top) / rect.height).toFixed(4)),
      t: Date.now() - startedAtRef.current,
    };
  }

  function toCanvas(point: SignaturePoint, rect: DOMRect) {
    return { x: point.x * rect.width, y: point.y * rect.height };
  }

  function handleDown(event: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    if (strokesRef.current.length === 0) startedAtRef.current = Date.now();

    drawingRef.current = true;
    strokesRef.current.push([pointFrom(event)]);
  }

  function handleMove(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current || disabled) return;

    const canvas = event.currentTarget;
    const ctx = canvas.getContext('2d');
    const stroke = strokesRef.current[strokesRef.current.length - 1];
    if (!ctx || !stroke) return;

    const rect = canvas.getBoundingClientRect();
    const previous = stroke[stroke.length - 1]!;
    const point = pointFrom(event);
    stroke.push(point);

    const from = toCanvas(previous, rect);
    const to = toCanvas(point, rect);
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    setEmpty(false);
  }

  function handleUp() {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    emit();
  }

  function emit() {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const points = strokesRef.current.reduce((sum, s) => sum + s.length, 0);
    if (points < MIN_POINTS) {
      onChange(null);
      return;
    }
    onChange({ dataUrl: canvas.toDataURL('image/png'), strokes: strokesRef.current });
  }

  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
    }
    strokesRef.current = [];
    setEmpty(true);
    onChange(null);
  }

  return (
    <div className="sig">
      <div className="sig__head">
        <span className="sig__label">
          {label}
          {!empty && <span className="sig__ok"> 서명됨</span>}
        </span>
        <button type="button" onClick={clear} disabled={disabled || empty}>
          지우기
        </button>
      </div>

      <canvas
        ref={canvasRef}
        className="sig__canvas"
        style={{ height, touchAction: 'none' }}
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerCancel={handleUp}
        onPointerLeave={handleUp}
        aria-label={`${label} 서명 입력`}
      />

      {empty && <p className="sig__hint">위 칸에 손가락 또는 펜으로 서명해 주세요.</p>}
    </div>
  );
}
